<template>
  <Section
    :id="id"
    :aria-label="t('search.paramSearchCriteria', { param: t('orders.orders') })"
    class="route-builder-order-search-builder search-builder"
    data-class="flex flex-gap-1 flex-wrap justify-content-between align-items-start"
  >
    <div
      class="filters flex flex-gap-1 flex-wrap flex-grow-1 align-items-start"
    >
      <template v-for="filter in filters" :key="filter">
        <component
          :is="availableFiltersMap.get(filter)"
          :id="`criteria-${filter}`"
          v-model="criteria"
          :ref-number-type="referenceNumbersMap.get(filter)"
        />
      </template>
    </div>

    <FilterSelect
      v-model="filters"
      :possible-options="possibleFilterOptions"
      append-to="body"
    />
  </Section>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import type { Component, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue } from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValues } from '@rygen/rygen-ui/i18n';
import {
  ClientReferenceNumberType,
  DropdownOption,
  Optional,
  ReferenceNumberTypeLevel,
} from '@rygen/rygen-ui/model';
import { distinct, notNull } from '@rygen/rygen-ui/util';

import { useLocationGrouping, useReferenceNumberTypes } from '@/composables';
import { OrderSearchCriteria, OrderSearchFilter } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import OrderCancellationReasonFilter from '@/components/order/filter/OrderCancellationReasonFilter.vue';
import OrderCreatedDateFilter from '@/components/order/filter/OrderCreatedDateFilter.vue';
import OrderCustomerRoutedFilter from '@/components/order/filter/OrderCustomerRoutedFilter.vue';
import OrderDeliveryAppointmentRequiredFilter from '@/components/order/filter/OrderDeliveryAppointmentRequiredFilter.vue';
import OrderDestinationFilter from '@/components/order/filter/OrderDestinationFilter.vue';
import OrderFreightAllowanceEnabledFilter from '@/components/order/filter/OrderFreightAllowanceEnabledFilter.vue';
import OrderGLCodeFilter from '@/components/order/filter/OrderGLCodeFilter.vue';
import OrderHandlingUnitFilter from '@/components/order/filter/OrderHandlingUnitFilter.vue';
import OrderHazmatFilter from '@/components/order/filter/OrderHazmatFilter.vue';
import OrderHotFilter from '@/components/order/filter/OrderHotFilter.vue';
import OrderOriginFilter from '@/components/order/filter/OrderOriginFilter.vue';
import OrderPickupAppointmentRequiredFilter from '@/components/order/filter/OrderPickupAppointmentRequiredFilter.vue';
import OrderProductNumberFilter from '@/components/order/filter/OrderProductNumberFilter.vue';
import OrderRequestedDeliveryFilter from '@/components/order/filter/OrderRequestedDeliveryFilter.vue';
import OrderRequestedPickupFilter from '@/components/order/filter/OrderRequestedPickupFilter.vue';
import OrderSwcFilter from '@/components/order/filter/OrderSwcFilter.vue';
import OrderWeightFilter from '@/components/order/filter/OrderWeightFilter.vue';
import AssignedUsersOrderFilter from '@/components/search/AssignedUsersOrderFilter.vue';
import DestinationLocationGroupsSearchFilter from '@/components/search/DestinationLocationGroupsSearchFilter.vue';
import DirectionFilter from '@/components/search/DirectionFilter.vue';
import EquipmentTypeFilter from '@/components/search/EquipmentTypeFilter.vue';
import FreightAllowanceFilter from '@/components/search/FreightAllowanceFilter.vue';
import IdFilter from '@/components/search/IdFilter.vue';
import OriginLocationGroupsSearchFilter from '@/components/search/OriginLocationGroupsSearchFilter.vue';
import ReferenceNumberFilter from '@/components/search/ReferenceNumberFilter.vue';
import RequestedModeFilter from '@/components/search/RequestedModeFilter.vue';
import SourceFilter from '@/components/search/SourceFilter.vue';
import TagFilter from '@/components/search/TagFilter.vue';
import FilterSelect from '@rygen/rygen-ui/components/search/FilterSelect.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const ORDER_FILTERS: Record<OrderSearchFilter, Optional<Component>> = {
  [OrderSearchFilter.CLIENT]: undefined,
  [OrderSearchFilter.ID]: IdFilter,
  [OrderSearchFilter.STATUS]: undefined,
  [OrderSearchFilter.ORIGIN]: OrderOriginFilter,
  [OrderSearchFilter.DESTINATION]: OrderDestinationFilter,
  [OrderSearchFilter.ORIGIN_LOCATION_GROUPS]: OriginLocationGroupsSearchFilter,
  [OrderSearchFilter.DESTINATION_LOCATION_GROUPS]:
    DestinationLocationGroupsSearchFilter,
  [OrderSearchFilter.REQUESTED_PICKUP]: OrderRequestedPickupFilter,
  [OrderSearchFilter.PICKUP_APPOINTMENT_REQUIRED]:
    OrderPickupAppointmentRequiredFilter,
  [OrderSearchFilter.REQUESTED_DELIVERY]: OrderRequestedDeliveryFilter,
  [OrderSearchFilter.DELIVERY_APPOINTMENT_REQUIRED]:
    OrderDeliveryAppointmentRequiredFilter,
  [OrderSearchFilter.CREATED_DATE]: OrderCreatedDateFilter,
  [OrderSearchFilter.GL_CODE]: OrderGLCodeFilter,
  [OrderSearchFilter.TAG]: TagFilter,
  [OrderSearchFilter.PRODUCT_NUMBER]: OrderProductNumberFilter,
  [OrderSearchFilter.HAZMAT]: OrderHazmatFilter,
  [OrderSearchFilter.DIRECTION]: DirectionFilter,
  [OrderSearchFilter.EQUIPMENT_TYPE]: EquipmentTypeFilter,
  [OrderSearchFilter.FREIGHT_ALLOWANCE_ENABLED]:
    OrderFreightAllowanceEnabledFilter,
  [OrderSearchFilter.FREIGHT_ALLOWANCE]: FreightAllowanceFilter,
  [OrderSearchFilter.ASSIGNED_USERS]: AssignedUsersOrderFilter,
  [OrderSearchFilter.CANCELLATION_REASON]: OrderCancellationReasonFilter,
  [OrderSearchFilter.REQUESTED_MODE]: RequestedModeFilter,
  [OrderSearchFilter.WEIGHT]: OrderWeightFilter,
  [OrderSearchFilter.HANDLING_UNIT]: OrderHandlingUnitFilter,
  [OrderSearchFilter.SOURCE]: SourceFilter,
  [OrderSearchFilter.SWC]: OrderSwcFilter,
  [OrderSearchFilter.CUSTOMER_ROUTED]: OrderCustomerRoutedFilter,
  [OrderSearchFilter.HOT]: OrderHotFilter,
};

const ELASTICSEARCH_ONLY_FILTERS = [
  OrderSearchFilter.FREIGHT_ALLOWANCE,
  OrderSearchFilter.WEIGHT,
  OrderSearchFilter.HANDLING_UNIT,
];

const LOCATION_GROUPING_ONLY_FILTERS = [
  OrderSearchFilter.ORIGIN_LOCATION_GROUPS,
  OrderSearchFilter.DESTINATION_LOCATION_GROUPS,
];

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: OrderSearchCriteria,
    default: () => new OrderSearchCriteria(),
  },
});
const { id, modelValue } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', modelValue: OrderSearchCriteria): void;
}>();

const isElasticSearchEnabled = useFlag('elasticsearch-tms-feat-flag');

const { isLocationGroupingEnabled } = useLocationGrouping();

const { selectedDomainId } = useCurrentUserStore();
const { t } = useI18n();
const { internalValue: criteria } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<OrderSearchCriteria>) =>
    new OrderSearchCriteria(opts),
});
const {
  lineItemReferenceNumberTypes,
  loadReferenceNumberTypes,
  orderReferenceNumberTypes,
} = useReferenceNumberTypes();

const orderSearchFilters = mapEnumToTranslatedDropdownValues(
  OrderSearchFilter,
  'OrderSearchFilter',
);

const filters: Ref<Set<OrderSearchFilter | string>> = ref(
  new Set([
    OrderSearchFilter.ORIGIN,
    OrderSearchFilter.DESTINATION,
    OrderSearchFilter.REQUESTED_PICKUP,
    OrderSearchFilter.REQUESTED_DELIVERY,
  ]),
);

const referenceNumbers: Ref<ClientReferenceNumberType[]> = computed(() => {
  const orderTypes = orderReferenceNumberTypes.value.data ?? [];
  const lineItemTypes = lineItemReferenceNumberTypes.value.data ?? [];

  return [...orderTypes, ...lineItemTypes].filter(distinct());
});

const referenceNumberNaturalIds: Ref<Set<string>> = computed(() => {
  const naturalIds = referenceNumbers.value
    .map((r) => r.referenceNumberType?.naturalId)
    .filter(notNull);
  return new Set(naturalIds);
});

const referenceNumberFilters = computed((): DropdownOption<string>[] => {
  return [...referenceNumberNaturalIds.value]
    .map((naturalId) => {
      const refNumType = referenceNumbersMap.value.get(naturalId);
      if (!refNumType) {
        return;
      }

      return {
        value: naturalId,
        label: refNumType.referenceNumberType?.name ?? '-',
      };
    })
    .filter(notNull);
});

const possibleFilterOptions = computed(() => {
  return orderSearchFilters
    .filter((opt) => {
      const filter = opt.value as OrderSearchFilter;
      return !!ORDER_FILTERS[filter];
    })
    .filter(
      (opt) =>
        !LOCATION_GROUPING_ONLY_FILTERS.includes(
          opt.value as OrderSearchFilter,
        ) || isLocationGroupingEnabled.value,
    )
    .concat(referenceNumberFilters.value);
});

const availableFiltersMap = computed(() => {
  const basicFilters = Object.entries(ORDER_FILTERS)
    .filter<[string, Component]>((value): value is [string, Component] => {
      const [_filterType, component] = value;
      return !!component;
    })
    .filter(([filterType, _component]) => {
      const orderSearchFilter = filterType as OrderSearchFilter;
      return (
        (!ELASTICSEARCH_ONLY_FILTERS.includes(orderSearchFilter) ||
          isElasticSearchEnabled.value) &&
        (!LOCATION_GROUPING_ONLY_FILTERS.includes(orderSearchFilter) ||
          isLocationGroupingEnabled.value)
      );
    });
  const refNumFilters: [string, Component][] = [
    ...referenceNumberNaturalIds.value,
  ].map((naturalId) => [naturalId, ReferenceNumberFilter]);
  const allFilters = [...basicFilters, ...refNumFilters];

  return new Map<string, Component>(allFilters);
});

const referenceNumbersMap: Ref<Map<string, ClientReferenceNumberType>> =
  computed(() => {
    const entries = referenceNumbers.value
      .map((refNum) => {
        const naturalId = refNum.referenceNumberType?.naturalId;
        if (naturalId) {
          return [naturalId, refNum] as [string, ClientReferenceNumberType];
        }
      })
      .filter(notNull);
    return new Map(entries);
  });

watch(
  criteria,
  (newVal: OrderSearchCriteria) => {
    if (newVal.orderId.length) {
      filters.value.add(OrderSearchFilter.ID);
    }
    if (
      newVal.originLocationId ||
      newVal.originLocationCode ||
      newVal.originCity ||
      newVal.originState ||
      newVal.originPostalCode ||
      newVal.originCountry
    ) {
      filters.value.add(OrderSearchFilter.ORIGIN);
    }
    if (
      newVal.destinationLocationId ||
      newVal.destinationLocationCode ||
      newVal.destinationCity ||
      newVal.destinationState ||
      newVal.destinationPostalCode ||
      newVal.destinationCountry
    ) {
      filters.value.add(OrderSearchFilter.DESTINATION);
    }
    if (isLocationGroupingEnabled.value) {
      if (newVal.originLocationGroupIds.length) {
        filters.value.add(OrderSearchFilter.ORIGIN_LOCATION_GROUPS);
      }
      if (newVal.destinationLocationGroupIds.length) {
        filters.value.add(OrderSearchFilter.DESTINATION_LOCATION_GROUPS);
      }
    }
    if (
      newVal.requestedPickupDateRange?.start ||
      newVal.requestedPickupDateRange?.end
    ) {
      filters.value.add(OrderSearchFilter.REQUESTED_PICKUP);
    }
    if (newVal.pickupAppointmentRequired != null) {
      filters.value.add(OrderSearchFilter.PICKUP_APPOINTMENT_REQUIRED);
    }
    if (
      newVal.requestedDeliveryDateRange?.start ||
      newVal.requestedDeliveryDateRange?.end
    ) {
      filters.value.add(OrderSearchFilter.REQUESTED_DELIVERY);
    }
    if (newVal.deliveryAppointmentRequired != null) {
      filters.value.add(OrderSearchFilter.DELIVERY_APPOINTMENT_REQUIRED);
    }
    if (newVal.createdDateRange?.start || newVal.createdDateRange?.end) {
      filters.value.add(OrderSearchFilter.CREATED_DATE);
    }
    if (newVal.referenceNumbers.length) {
      newVal.referenceNumbers.forEach((refNumber) => {
        if (refNumber.typeNaturalId !== undefined) {
          filters.value.add(refNumber.typeNaturalId);
        }
      });
    }
    if (newVal.glCodes.length) {
      filters.value.add(OrderSearchFilter.GL_CODE);
    }
    if (newVal.productNumber) {
      filters.value.add(OrderSearchFilter.PRODUCT_NUMBER);
    }
    if (newVal.tags.length) {
      filters.value.add(OrderSearchFilter.TAG);
    }
    if (newVal.assignedUsers.length) {
      filters.value.add(OrderSearchFilter.ASSIGNED_USERS);
    }
    if (newVal.hazmat != null) {
      filters.value.add(OrderSearchFilter.HAZMAT);
    }
    if (newVal.directions.length) {
      filters.value.add(OrderSearchFilter.DIRECTION);
    }
    if (newVal.equipmentTypes.length) {
      filters.value.add(OrderSearchFilter.EQUIPMENT_TYPE);
    }
    if (newVal.freightAllowanceEnabled != null) {
      filters.value.add(OrderSearchFilter.FREIGHT_ALLOWANCE_ENABLED);
    }
    if (newVal.cancellationReason) {
      filters.value.add(OrderSearchFilter.CANCELLATION_REASON);
    }
    if (newVal.requestedMode.length) {
      filters.value.add(OrderSearchFilter.REQUESTED_MODE);
    }
    if (newVal.source) {
      filters.value.add(OrderSearchFilter.SOURCE);
    }
    if (isElasticSearchEnabled.value) {
      if (newVal.weight) {
        filters.value.add(OrderSearchFilter.WEIGHT);
      }
      if (newVal.handlingUnit) {
        filters.value.add(OrderSearchFilter.HANDLING_UNIT);
      }
      if (newVal.freightAllowance != null) {
        filters.value.add(OrderSearchFilter.FREIGHT_ALLOWANCE);
      }
    }
  },
  { deep: true, immediate: true },
);

watch(
  filters,
  (
    newVal: Set<OrderSearchFilter | string>,
    oldVal: Set<OrderSearchFilter | string>,
  ) => {
    const removedFilters = [...oldVal].filter((f) => !newVal.has(f));
    removedFilters.forEach((filter) => {
      if (filter === OrderSearchFilter.ID) {
        criteria.value.orderId = [];
      } else if (filter === OrderSearchFilter.ORIGIN) {
        criteria.value.originLocationId = undefined;
        criteria.value.originLocationCode = undefined;
        criteria.value.originCity = undefined;
        criteria.value.originState = undefined;
        criteria.value.originPostalCode = undefined;
        criteria.value.originCountry = undefined;
      } else if (filter === OrderSearchFilter.DESTINATION) {
        criteria.value.destinationLocationId = undefined;
        criteria.value.destinationLocationCode = undefined;
        criteria.value.destinationCity = undefined;
        criteria.value.destinationState = undefined;
        criteria.value.destinationPostalCode = undefined;
        criteria.value.destinationCountry = undefined;
      } else if (filter === OrderSearchFilter.ORIGIN_LOCATION_GROUPS) {
        criteria.value.originLocationGroupIds = [];
      } else if (filter === OrderSearchFilter.DESTINATION_LOCATION_GROUPS) {
        criteria.value.destinationLocationGroupIds = [];
      } else if (filter === OrderSearchFilter.REQUESTED_PICKUP) {
        criteria.value.requestedPickupDateRange = undefined;
      } else if (filter === OrderSearchFilter.PICKUP_APPOINTMENT_REQUIRED) {
        criteria.value.pickupAppointmentRequired = null;
      } else if (filter === OrderSearchFilter.REQUESTED_DELIVERY) {
        criteria.value.requestedDeliveryDateRange = undefined;
      } else if (filter === OrderSearchFilter.DELIVERY_APPOINTMENT_REQUIRED) {
        criteria.value.deliveryAppointmentRequired = null;
      } else if (filter === OrderSearchFilter.CREATED_DATE) {
        criteria.value.createdDateRange = undefined;
      } else if (filter === OrderSearchFilter.GL_CODE) {
        criteria.value.glCodes = [];
      } else if (filter === OrderSearchFilter.TAG) {
        criteria.value.tags = [];
      } else if (filter === OrderSearchFilter.ASSIGNED_USERS) {
        criteria.value.assignedUsers = [];
      } else if (filter === OrderSearchFilter.PRODUCT_NUMBER) {
        criteria.value.productNumber = undefined;
      } else if (filter === OrderSearchFilter.HAZMAT) {
        criteria.value.hazmat = null;
      } else if (filter === OrderSearchFilter.DIRECTION) {
        criteria.value.directions = [];
      } else if (filter === OrderSearchFilter.EQUIPMENT_TYPE) {
        criteria.value.equipmentTypes = [];
      } else if (filter === OrderSearchFilter.FREIGHT_ALLOWANCE_ENABLED) {
        criteria.value.freightAllowanceEnabled = null;
      } else if (filter === OrderSearchFilter.FREIGHT_ALLOWANCE) {
        criteria.value.freightAllowance = undefined;
      } else if (filter === OrderSearchFilter.CANCELLATION_REASON) {
        criteria.value.cancellationReason = undefined;
      } else if (filter === OrderSearchFilter.REQUESTED_MODE) {
        criteria.value.requestedMode = [];
      } else if (filter === OrderSearchFilter.WEIGHT) {
        criteria.value.weight = undefined;
      } else if (filter === OrderSearchFilter.HANDLING_UNIT) {
        criteria.value.handlingUnit = undefined;
      } else if (filter === OrderSearchFilter.SOURCE) {
        criteria.value.source = undefined;
      }
    });
  },
);

watch(selectedDomainId, () => {
  // When domain changes, clear domain-specific filters
  criteria.value.ownerNaturalIds = [];
  criteria.value.id = undefined;
  criteria.value.orderId = [];
  criteria.value.originLocationId = undefined;
  criteria.value.originLocationCode = undefined;
  criteria.value.destinationLocationId = undefined;
  criteria.value.destinationLocationCode = undefined;
  criteria.value.savedSearchId = undefined;
  criteria.value.productNumber = undefined;
  criteria.value.referenceNumbers = [];
  criteria.value.glCodes = [];
  criteria.value.tags = [];
  criteria.value.assignedUsers = [];
});

watch(
  selectedDomainId,
  () => {
    loadReferenceNumberTypes({
      level: ReferenceNumberTypeLevel.ORDER,
      bypassCache: true,
    });
    loadReferenceNumberTypes({
      level: ReferenceNumberTypeLevel.LINE_ITEM,
      bypassCache: true,
    });
  },
  { immediate: true },
);
</script>

<style>
.route-builder-order-search-builder {
  display: flex;
  flex-direction: column;
  gap: var(--gap-1);

  .section-data {
    gap: var(--sp-6);

    .filters {
      max-width: 100%;

      .popup-inputgroup {
        .popup-inputgroup-card {
          width: max-content;
          top: calc((var(--sp-8) * -1) - 1px);
        }
      }
    }
  }
}

body.p-overflow-hidden:has(.route-builder-order-search-builder) {
  & > .popup-inputgroup-card {
    z-index: 9999;
  }

  .p-autocomplete-overlay,
  .p-confirmpopup,
  .p-datepicker-panel,
  .p-menu-overlay,
  .p-multiselect-overlay,
  .p-popover,
  .p-select-overlay {
    /* .p-component for extra specificity */
    &.p-component {
      z-index: 99990 !important;
    }
  }
}
</style>
